import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays } from 'lucide-react'
import { Skeleton } from '~/components/ui/skeleton'
import { cn } from '~/lib/utils'
import { api } from '~/lib/tuyau'
import { PeriodsTable } from './-periods-table'
import { periodDot, shortLabel, type SchoolHolidayPeriod } from './-shared'
import { YearTimeline } from './-year-timeline'

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border bg-card px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
  </div>
)

const VacancesScolairesPage = () => {
  const { data, isPending, error } = useQuery(api.calendar.schoolHolidays.queryOptions({}))

  const periods: SchoolHolidayPeriod[] = data?.periods ?? []
  const schoolYears = [...new Set(periods.map((period) => period.schoolYear))].filter(
    (schoolYear): schoolYear is string => schoolYear !== null,
  )
  const labels = [...new Set(periods.map((period) => period.label))]
  const fetchedAt = periods.find((period) => period.fetchedAt !== null)?.fetchedAt ?? null

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <CalendarDays className="size-5" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Vacances scolaires</h1>
            <p className="text-sm text-muted-foreground">
              Zones A, B et C — source data.education.gouv.fr
              {fetchedAt !== null &&
                `, récupérées le ${new Date(fetchedAt).toLocaleDateString('fr-FR')}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {labels.map((label) => (
            <span
              key={label}
              className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground"
            >
              <span className={cn('size-2 rounded-full', periodDot(label))} />
              {shortLabel(label)}
            </span>
          ))}
        </div>
      </header>

      {isPending && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {error !== null && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Impossible de charger les périodes.
        </p>
      )}

      {!isPending && periods.length === 0 && (
        <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          Aucune période en base. Lance{' '}
          <code className="rounded bg-muted px-1.5 py-0.5">
            node ace calendar:sync-school-holidays
          </code>
          .
        </p>
      )}

      {periods.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Périodes" value={String(periods.length)} />
            <Stat label="Années scolaires" value={String(schoolYears.length)} />
            <Stat label="Zones" value="A · B · C" />
            <Stat
              label="Jours / zone / an"
              value={String(
                Math.round(
                  periods.reduce((total, period) => total + period.days, 0) /
                    Math.max(1, schoolYears.length * 3),
                ),
              )}
            />
          </div>

          {schoolYears.map((schoolYear) => (
            <YearTimeline
              key={schoolYear}
              schoolYear={schoolYear}
              periods={periods.filter((period) => period.schoolYear === schoolYear)}
            />
          ))}

          <PeriodsTable periods={periods} />
        </>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/vacances-scolaires/')({
  component: VacancesScolairesPage,
})
